<template>
  <div class="choropleth">
    <div class="mapWrapper">
      <svg
        v-if="height !== null && height > 0"
        width="100%"
        :height="height"
        :viewBox="`0 0 ${width} ${height}`"
        ref="svg"
        v-show="isReady"
      >
        <g class="map">
          <path
            v-for="(polygon, index) in polygons"
            :key="'polygon' + index"
            class="polygon"
            :d="polygon.path"
          />
        </g>
        <g class="map" v-if="markers && markers.length > 0">
          <g v-for="(marker, index) in validMarkers" :key="'marker-' + index">
            <text
              :y="getMarkerTextY(marker)"
              :x="getMarkerX(marker)"
              :text-anchor="marker.anchor || 'middle'"
              class="marker shaddow"
              :stroke-width="2"
              :font-size="14"
            >
              {{ marker.label }}
            </text>
            <text
              :y="getMarkerTextY(marker)"
              :x="getMarkerX(marker)"
              :text-anchor="marker.anchor || 'middle'"
              class="marker"
              :font-size="14"
            >
              {{ marker.label }}
            </text>
            <circle
              class="marker-dot"
              :cx="getMarkerX(marker)"
              :cy="getMarkerY(marker)"
              :r="markerSize"
            />
          </g>
        </g>
      </svg>
      <div v-if="!isReady || height === null" class="loading">
        Loading map...
      </div>
    </div>
  </div>
</template>

<script>
import * as d3 from "d3";
import debounce from "debounce";

d3.formatDefaultLocale({
  decimal: ",",
  thousands: ".",
  grouping: [3],
  currency: ["", "€"],
  dateTime: "%a %b %e %X %Y",
  date: "%d.%m.%Y",
  time: "%H:%M:%S",
  periods: ["AM", "PM"],
  days: [
    "Sonntag",
    "Montag",
    "Dienstag",
    "Mittwoch",
    "Donnerstag",
    "Freitag",
    "Samstag",
  ],
  shortDays: ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"],
  months: [
    "Januar",
    "Februar",
    "März",
    "April",
    "Mai",
    "Juni",
    "Juli",
    "August",
    "September",
    "Oktober",
    "November",
    "Dezember",
  ],
  shortMonths: [
    "Jan",
    "Feb",
    "Mär",
    "Apr",
    "Mai",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Okt",
    "Nov",
    "Dez",
  ],
});

export default {
  name: "LocatorMap",
  props: {
    options: {
      type: Object,
      default: () => ({}),
    },
    geoJson: {
      type: Object,
      default: null,
    },
    markers: {
      type: Array,
      default: () => [],
    },
    maxHeight: {
      type: Number,
      default: 700,
    },
    value: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      projection: d3.geoMercator(),
      polygons: [],
      height: null,
      width: 600,
      hover: null,
      strokeWidth: 1.0,
      isProjectionReady: false,
      isReady: false,
      pathGenerator: null,
      defaultOptions: {
        title: "Stärkste Partei",
        type: "heatmap",
        regionKey: "WKR_NR",
        regionLabel: "WKR_NAME",
        colors: [
          { label: "SPD", id: "SPD", color: "#c51d1e" },
          { label: "CDU", id: "CDU", color: "#333" },
          { label: "CSU", id: "CSU", color: "#333" },
          { label: "Grüne", id: "GRÜNE", color: "#0c9941" },
          { label: "FDP", id: "FDP", color: "#f6c909" },
          { label: "AfD", id: "AfD", color: "#2b7ab4" },
          { label: "Linke", id: "DIE LINKE", color: "#bb4894" },
          { label: "Die PARTEI", id: "Die PARTEI", color: "#B92837" },
          { label: "FW", id: "FREIE WÄHLER", color: "#b5b9bc" },
          { label: "Piraten", id: "PIRATEN", color: "#f39200" },
          { label: "REP", id: "REP", color: "#a07e26" },
          { label: "NPD", id: "NPD", color: "#a07e26" },
          { label: "Einheit", id: "EINHEIT", color: "#a284c7" },
          { label: "Volt", id: "Volt", color: "#502379" },
          { label: "Humanisten", id: "HUMANISTEN", color: "#d90368" },
          { label: "DKP", id: "DKP", color: "red" },
        ],
        colorScale: ["#fff", "#111"],
        legend: true,
        zoom: true,
      },
    };
  },
  computed: {
    validMarkers() {
      if (!this.markers || !this.isProjectionReady) return [];

      return this.markers.filter((marker) => {
        return (
          marker &&
          typeof marker.lat === "number" &&
          typeof marker.long === "number" &&
          !isNaN(marker.lat) &&
          !isNaN(marker.long) &&
          marker.lat >= -90 &&
          marker.lat <= 90 &&
          marker.long >= -180 &&
          marker.long <= 180
        );
      });
    },

    markerSize() {
      return this.width / 25;
    },

    settings() {
      return Object.assign({}, this.defaultOptions, this.options);
    },

    path() {
      // Verwende den gespeicherten pathGenerator
      return this.pathGenerator || d3.geoPath().projection(this.projection);
    },
  },
  methods: {
    fitProjection() {
      try {
        if (
          !this.geoJson ||
          !this.geoJson.features ||
          this.geoJson.features.length === 0
        ) {
          console.warn("GeoJSON not ready for projection");
          this.isReady = false;
          return;
        }

        // Für die initiale Berechnung brauchen wir ein temporäres Element
        // Wenn svg ref nicht existiert, nutzen wir die default width
        let containerWidth = 600;

        if (this.$refs.svg) {
          const rect = this.$refs.svg.getBoundingClientRect();
          containerWidth = rect.width || 600;
        } else if (this.$el) {
          // Fallback: Nutze die Breite des Container-Elements
          const rect = this.$el.getBoundingClientRect();
          containerWidth = rect.width || 600;
        }

        this.width = containerWidth;

        // Erstelle neue Projektion
        this.projection = d3.geoMercator();
        this.pathGenerator = d3.geoPath().projection(this.projection);

        // Berechne Bounds der GeoJSON-Features
        const bounds = d3.geoBounds(this.geoJson);
        const [[lon0, lat0], [lon1, lat1]] = bounds;

        // Berechne Zentrum
        const centerLon = (lon0 + lon1) / 2;
        const centerLat = (lat0 + lat1) / 2;

        // Setze Projektion auf Zentrum
        this.projection.center([centerLon, centerLat]);

        // Berechne initiale Scale
        const distance = Math.sqrt(
          Math.pow(lon1 - lon0, 2) + Math.pow(lat1 - lat0, 2)
        );
        const initialScale = 150 / distance;
        this.projection.scale(initialScale);

        // Berechne Pixel-Bounds
        const pixelBounds = this.pathGenerator.bounds(this.geoJson);
        const [[x0, y0], [x1, y1]] = pixelBounds;

        const pixelWidth = x1 - x0;
        const pixelHeight = y1 - y0;

        // Berechne finale Scale basierend auf Breite
        const padding = 20;
        const scale = initialScale * ((this.width - 2 * padding) / pixelWidth);

        // Setze Höhe basierend auf Aspect Ratio
        let calculatedHeight = Math.round(
          (pixelHeight / pixelWidth) * (this.width - 2 * padding) + 2 * padding
        );

        // Begrenze Höhe
        if (this.maxHeight && calculatedHeight > this.maxHeight) {
          calculatedHeight = this.maxHeight;
        }

        // Setze finale Scale und Translation
        this.projection.scale(scale);
        this.projection.translate([this.width / 2, calculatedHeight / 2]);

        // WICHTIG: Setze height erst NACH allen Berechnungen
        this.height = calculatedHeight;

        console.log("Calculated dimensions:", this.width, "x", this.height);
        console.log("Projection scale:", this.projection.scale());
        console.log("Projection center:", this.projection.center());

        // Generate polygons
        this.polygons = this.geoJson.features
          .map((feature, index) => {
            try {
              const pathString = this.pathGenerator(feature);
              return {
                path: pathString,
                feature: feature,
                id: index,
              };
            } catch (error) {
              console.warn("Error generating path for feature:", error);
              return {
                path: "",
                feature: feature,
                id: index,
              };
            }
          })
          .filter((polygon) => polygon.path);

        console.log("Number of polygons:", this.polygons.length);

        this.isProjectionReady = true;
        this.isReady = true;
        console.log("Projection fitted successfully");
      } catch (error) {
        console.error("Error fitting projection:", error);
        this.isProjectionReady = false;
        this.isReady = false;
      }
    },

    getMarkerX(marker) {
      try {
        const projected = this.projection([marker.long, marker.lat]);
        return projected && projected[0] ? projected[0] : 0;
      } catch (error) {
        console.warn("Error projecting marker X:", error);
        return 0;
      }
    },

    getMarkerY(marker) {
      try {
        const projected = this.projection([marker.long, marker.lat]);
        const y = projected && projected[1] ? projected[1] : 0;
        console.log("getMarkerY:", y);
        return y;
      } catch (error) {
        console.warn("Error projecting marker Y:", error);
        return 0;
      }
    },

    getMarkerTextY(marker) {
      const baseY = this.getMarkerY(marker);
      const offset = -10;
      return baseY + offset;
    },

    project(coordinates) {
      try {
        if (!coordinates || coordinates.length !== 2) return [0, 0];

        const [long, lat] = coordinates;
        if (typeof lat !== "number" || typeof long !== "number") return [0, 0];
        if (isNaN(lat) || isNaN(long)) return [0, 0];

        const result = this.projection([long, lat]);
        return result || [0, 0];
      } catch (error) {
        console.warn("Error in projection:", error);
        return [0, 0];
      }
    },

    format(num) {
      return String(num).replace(".", ",");
    },

    getColor(value) {
      return this.colorScale ? this.colorScale(value * 12) : "#ddd";
    },

    getFill(polygon) {
      let color = "#ddd";

      try {
        if (this.settings.type === "heatmap") {
          if (!polygon.data) {
            color = "url(#nodata)";
          } else {
            color = this.colorScale
              ? this.colorScale(polygon.data[1])
              : "url(#nodata)";
          }
        } else if (this.settings.type === "category") {
          if (polygon.data && polygon.data.category === "nodata") {
            color = "url(#nodata)";
          }
          if (this.settings.colors && polygon.data) {
            const match = this.settings.colors.find(
              (party) => party.id === polygon.data.category
            );
            color = match ? match.color : "url(#nodata)";
          } else {
            color = "url(#nodata)";
          }
        }
      } catch (error) {
        console.warn("Error getting fill color:", error);
      }

      return color;
    },

    mouseover(polygon) {
      try {
        const centroid = this.pathGenerator.centroid(polygon.feature);

        this.hover = {
          region: polygon.feature.properties[this.settings.regionLabel],
          value: polygon.data ? polygon.data[1] : null,
          x: this.clamp(Math.round(centroid[0]), 60, this.width - 60),
          y: this.clamp(Math.round(centroid[1]), 60, this.height),
          path: this.pathGenerator(polygon.feature),
        };
      } catch (error) {
        console.warn("Error in mouseover:", error);
      }
    },

    clamp(num, min, max) {
      return num <= min ? min : num >= max ? max : num;
    },

    mouseleave() {
      this.hover = null;
    },

    selectRegion(polygon) {
      try {
        this.$emit("input", {
          id: polygon.data ? polygon.data.regionKey : null,
          name: polygon.data ? polygon.data.regionName : null,
        });
      } catch (error) {
        console.warn("Error selecting region:", error);
      }
    },

    resetRegion() {
      this.$emit("input", {
        id: 0,
        name: "Wahlkreis suchen",
      });
    },

    cssGradient(colors) {
      if (!colors || colors.length < 2) return "";
      return `background: linear-gradient(90deg, ${colors[0]} 0%, ${colors[1]} 100%);`;
    },
  },

  watch: {
    geoJson: {
      handler(newVal) {
        if (newVal && newVal.features && newVal.features.length > 0) {
          console.log("GeoJSON changed, scheduling fitProjection");
          this.$nextTick(() => {
            this.fitProjection();
          });
        }
      },
      immediate: true,
    },

    markers: {
      handler() {
        console.log("Markers changed:", this.markers);
      },
      deep: true,
    },
  },

  mounted() {
    console.log("LocatorMap mounted");

    this.$nextTick(() => {
      if (this.geoJson) {
        this.fitProjection();
      }
    });

    // Resize listener with debounce
    const debouncedResize = debounce(() => {
      if (this.geoJson) {
        this.fitProjection();
      }
    }, 250);

    window.addEventListener("resize", debouncedResize);

    // Cleanup
    this.$once("hook:beforeDestroy", () => {
      window.removeEventListener("resize", debouncedResize);
    });
  },
};
</script>

<style scoped lang="scss">
.choropleth {
  box-sizing: border-box;
  position: relative;

  .mapWrapper {
    position: relative;
    min-height: 100px; // Verhindert Layout-Shift während des Ladens
  }

  .loading {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 200px;
    color: #666;
    font-style: italic;
  }

  svg {
    display: block;
  }

  .map {
    .polygon {
      stroke: #333;
      stroke-width: 1;
      fill: #e0e0e0;
    }

    .marker-dot {
      pointer-events: none;
      fill: #a82424;
      stroke: #fff;
      stroke-width: 1.5;
    }

    .marker {
      pointer-events: none;
      font-family: "DV Source Sans 3", "FAZ Gold", Helvetica, Arial, sans-serif;
      font-size: 14px;
      fill: #fff;

      &.shaddow {
        fill: rgba(17, 17, 17, 1);
        stroke: rgba(17, 17, 17, 1);
      }
    }
  }
}
</style>
